import React, { useCallback, useEffect, useRef, useState } from "react";
import { Play, Volume2, VolumeX } from "lucide-react";
import useSound from "../../hooks/useSound";
import { SOUND_PROFILES } from "../../config/soundProfiles";
import DetailSheet from "../session/generate/DetailSheet";
import { Button } from "../ui/button";
import { Slider } from "../ui/slider";
import { Field, OptionList } from "../profile/ProfileParts";
import { cn } from "../../lib/utils";
import { AUDIO_OUTPUTS, resolveAudioOutput } from "../../audio/audioOutput";

export const CUE_GROUPS = [
  {
    label: "Phases",
    cues: [
      { type: "warmup", label: "Warm-up", when: "Start of the warm-up" },
      { type: "work", label: "Work", when: "Each work phase" },
      { type: "rest", label: "Rest", when: "Each rest between rounds" },
      { type: "set_rest", label: "Set rest", when: "The longer rest between sets" },
      { type: "cooldown", label: "Cool-down", when: "Start of the cool-down" },
    ],
  },
  {
    label: "Countdown & end",
    cues: [
      { type: "countdown_tick", label: "Tick", when: "Last 3 seconds of each phase, if ticks are on" },
      { type: "halfway", label: "Halfway", when: "Midway through timed phases of 10 seconds or more" },
      { type: "end", label: "Finish", when: "Session complete" },
    ],
  },
  {
    label: "Controls",
    cues: [
      { type: "pause", label: "Pause", when: "When you pause" },
      { type: "resume", label: "Resume", when: "When you carry on" },
    ],
  },
];

// A compressed round: the countdown into work, into rest, then the finish.
const TICK_GAP_MS = 600;
const PHASE_GAP_MS = 1400;
export const SAMPLE_ROUND = [
  "countdown_tick", "countdown_tick", "countdown_tick", "work",
  "countdown_tick", "countdown_tick", "countdown_tick", "rest",
  "countdown_tick", "countdown_tick", "countdown_tick", "end",
];

/** How long a cue's notes and rests run in a sound set, in ms. */
export const cueDurationMs = (profile, type) => {
  const steps = SOUND_PROFILES[profile]?.sounds?.[type] || [];
  const seconds = steps.reduce((sum, step) => sum + (step.duration ?? step.rest ?? 0), 0);
  return Math.round(seconds * 1000);
};

const PROFILE_OPTIONS = Object.entries(SOUND_PROFILES).map(([value, p]) => ({
  value,
  label: p.label,
  description: p.description,
}));

/**
 * Sounds (issue #86): volume, the sound set, and the cue list. Picking a set
 * plays a short sample of it; each cue says when it plays and has its own
 * play button; "Play a sample round" runs a countdown into work, rest and the
 * finish, lighting each cue's row as it sounds. With a linked timer it also
 * holds the default for where cues play while the timer follows a session
 * (issue #45); the Coach can change it mid-session.
 */
const SoundsSheet = ({ open, onClose, audio, updateAudio, hasTimer = false }) => {
  const playSound = useSound();
  const [playing, setPlaying] = useState(null); // cue type currently lit
  const [roundPlaying, setRoundPlaying] = useState(false);
  const timers = useRef([]);

  const stop = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setPlaying(null);
    setRoundPlaying(false);
  }, []);
  useEffect(() => stop, [stop]);
  useEffect(() => {
    if (!open) stop();
  }, [open, stop]);
  // Keep the cue that's sounding in view during a sample round.
  useEffect(() => {
    if (!roundPlaying || !playing) return;
    document
      .querySelector(`[data-cue="${playing}"]`)
      ?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [roundPlaying, playing]);

  // Plays a run of cues at the given offsets, lighting each row while it sounds.
  const schedule = useCallback(
    (steps, profile, onDone) => {
      stop();
      steps.forEach(({ type, at }) => {
        timers.current.push(
          setTimeout(() => {
            playSound(type, { preview: true, profile });
            setPlaying(type);
          }, at),
          setTimeout(
            () => setPlaying((p) => (p === type ? null : p)),
            at + Math.max(250, cueDurationMs(profile, type)),
          ),
        );
      });
      const end = steps.length ? steps[steps.length - 1].at + 600 : 0;
      timers.current.push(setTimeout(() => onDone?.(), end));
    },
    [playSound, stop],
  );

  const playCue = (type) => schedule([{ type, at: 0 }], audio.profile);

  const playRound = () => {
    let at = 0;
    const steps = SAMPLE_ROUND.map((type) => {
      const step = { type, at };
      // Let a long phase cue (Original's are) finish before the next tick.
      at += type === "countdown_tick" ? TICK_GAP_MS : Math.max(PHASE_GAP_MS, cueDurationMs(audio.profile, type) + 500);
      return step;
    });
    schedule(steps, audio.profile, () => setRoundPlaying(false));
    setRoundPlaying(true); // after schedule, which clears any run in progress
  };

  const pickProfile = (profile) => {
    updateAudio({ profile });
    schedule(
      ["countdown_tick", "countdown_tick", "countdown_tick", "work"].map((type, i) => ({
        type,
        at: i * TICK_GAP_MS,
      })),
      profile,
    );
  };

  const setName = SOUND_PROFILES[audio.profile]?.label || audio.profile;

  return (
    <DetailSheet open={open} onOpenChange={(o) => !o && onClose()} title="Sounds">
      <Field label="Volume" aside={audio.muted ? "Muted" : `${Math.round(audio.volume * 100)}%`}>
        <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3">
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => updateAudio({ muted: !audio.muted })}
            aria-label={audio.muted ? "Unmute" : "Mute"}
            aria-pressed={audio.muted}
          >
            {audio.muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </Button>
          <Slider
            value={[audio.volume * 100]}
            onValueChange={([v]) => updateAudio({ volume: v / 100 })}
            max={100}
            min={0}
            step={5}
            disabled={audio.muted}
            aria-label="Volume"
            className={audio.muted ? "opacity-50" : ""}
          />
        </div>
      </Field>

      {hasTimer && (
        <Field label="Sound plays on" aside="With a Khronos Timer">
          <OptionList
            options={AUDIO_OUTPUTS}
            value={resolveAudioOutput(audio.output)}
            onChange={(output) => updateAudio({ output })}
          />
        </Field>
      )}

      <Field label="Sound set" aside="Tap to hear it">
        <OptionList options={PROFILE_OPTIONS} value={audio.profile} onChange={pickProfile} />
      </Field>

      <Field label="Cues" aside={setName}>
        <Button
          type="button"
          className="h-11 w-full gap-2"
          onClick={roundPlaying ? stop : playRound}
          disabled={audio.muted}
        >
          <Play className="h-4 w-4" />
          {roundPlaying ? "Stop" : "Play a sample round"}
        </Button>
        {audio.muted && <p className="text-xs text-muted-foreground">Sounds are muted. Unmute to hear them.</p>}
        {CUE_GROUPS.map((group) => (
          <div key={group.label} className="grid gap-1.5 pt-1">
            <h3 className="px-0.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {group.label}
            </h3>
            <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-background">
              {group.cues.map((cue) => {
                const lit = playing === cue.type;
                return (
                  <div
                    key={cue.type}
                    data-cue={cue.type}
                    data-playing={lit || undefined}
                    className={cn(
                      "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-3 py-2.5 transition-colors",
                      lit && "bg-primary/10",
                    )}
                  >
                    <span className="min-w-0">
                      <span className={cn("block text-sm font-medium", lit && "text-primary")}>{cue.label}</span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">{cue.when}</span>
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => playCue(cue.type)}
                      disabled={audio.muted}
                      aria-label={`Play ${cue.label}`}
                      className={cn("h-9 w-9", lit && "border-primary text-primary")}
                    >
                      <Play className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </Field>
    </DetailSheet>
  );
};

export default SoundsSheet;
